import {
  GlobalPositionStrategy,
  NoopScrollStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import { isMobile } from '@libraries/ui-state';
import { select, Store } from '@ngrx/store';
import {
  GlobalMessageComponent,
  GlobalMessageConfig,
} from './global-message.component';

@Injectable({
  providedIn: 'root',
})
export class GlobalMessageOpenerService {
  private positionStrategy = new GlobalPositionStrategy().centerHorizontally();
  private scrollStrategy = new NoopScrollStrategy();
  private refs: OverlayRef[] = [];
  private desktopBottomOffset = 60;
  private bottomOffset = this.desktopBottomOffset;

  private get fromBottom(): number {
    return this.refs.reduce(
      (fromBottom, ref) => (fromBottom += ref.overlayElement.offsetHeight),
      this.bottomOffset
    );
  }

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private store: Store
  ) {
    this.store.pipe(select(isMobile)).subscribe((mobile) => {
      this.bottomOffset = mobile ? 0 : this.desktopBottomOffset;
      this.repositionAll();
    });
  }

  public open(config: GlobalMessageConfig) {
    // in case someone tries to open two at a time
    setTimeout(() => {
      // create and position overlay
      const overlayRef = this.overlay.create({
        positionStrategy: this.positionStrategy.bottom(`${this.fromBottom}px`),
        scrollStrategy: this.scrollStrategy,
        panelClass: this.refs.length > 0 ? 'animated-margins' : '',
      });

      // make sure onClose and onAction close the message first
      const closeCb = config.onClose;
      const actionCb = config.onAction;
      config.onAction = () => this.onClose(overlayRef, actionCb);
      config.onClose = () => this.onClose(overlayRef, closeCb);

      // create injector for our new message component and project component in portal
      const injector = Injector.create({
        parent: this.injector,
        providers: [{ provide: GlobalMessageConfig, useValue: config }],
      });
      const message = new ComponentPortal(
        GlobalMessageComponent,
        null,
        injector
      );

      // attach overlay and update our refs list
      this.refs.push(overlayRef);
      overlayRef.attach(message);
    });
  }

  private onClose(overlayRef: OverlayRef, callback?: () => any) {
    this.refs = this.refs.filter((ref) => ref !== overlayRef);
    overlayRef.dispose();
    this.repositionAll();
    if (callback) callback();
  }

  private repositionAll() {
    let prev: OverlayRef;
    let fromBottom = this.bottomOffset;
    this.refs.forEach((ref) => {
      if (prev) fromBottom += prev.overlayElement.offsetHeight;
      ref.overlayElement.style.marginBottom = `${fromBottom}px`;
      prev = ref;
    });
  }
}
